import { useRef, useState } from 'react';

import EventsClient from 'lib/clients/EventsClient';
import { CapturedVideoData, MediaRecorderManager } from 'lib/clients/mediaRecorder';
import { captureException, captureMessage } from 'lib/reporting';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { SelfieVideoCaptureMethod } from 'verify/lib/interfaces/CaptureOption';

const VIDEO_MAX_RECORDING_TIME = 120_000;

interface UseVideoCaptureProps {
  enableVideoRecording: boolean;
  videoCaptureMethods: SelfieVideoCaptureMethod[];
  videoSessionJwt?: string | null;
  captureIsDone: boolean;
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleRestartStep?: (handleRestartStepArgs?: { timeExceeded?: boolean }) => void;
}

export const useVideoCapture = ({
  enableVideoRecording,
  videoCaptureMethods,
  videoSessionJwt,
  captureIsDone,
  handleFailed,
  handleRestartStep,
}: UseVideoCaptureProps) => {
  // Use a ref for this variable because it's referenced in runTick timeout
  const videoReady = useRef<boolean>(!enableVideoRecording);

  const [capturedVideoData, setCapturedVideoData] = useState<CapturedVideoData | undefined>(
    undefined,
  );
  const [mediaRecorderSubId, setMediaRecorderSubId] = useState<string | undefined>(undefined);

  const initMediaRecorderClient = (stream: MediaStream, video: HTMLVideoElement) => {
    const mediaRecorderClient = MediaRecorderManager.getNewClient(videoCaptureMethods);

    if (enableVideoRecording && !mediaRecorderClient.isSupported()) {
      handleFailed('MediaRecorder not supported', 'load');
      return;
    }

    const subId = mediaRecorderClient.init({
      stream,
      video,
      jwt: videoSessionJwt,
      maxRecordingTime: VIDEO_MAX_RECORDING_TIME,
      onError: (e: string | Error) => {
        if (MediaRecorderManager.shouldReinitializeWithUpload(e, videoCaptureMethods)) {
          MediaRecorderManager.destroy();
          initMediaRecorderClient(stream, video);
        } else if (MediaRecorderManager.videoCanBeReattempted()) {
          handleRestartStep?.();
        } else {
          handleFailed('mediaRecorderClient failed', 'capture');
        }

        EventsClient.logAmplitude('selfie.video.errored', {
          message: typeof e === 'string' ? e : e?.message,
        });

        if (e instanceof Error) {
          captureException(e, 'error');
        } else {
          captureMessage(`Unexpected error: ${JSON.stringify(e)}`, 'error');
        }
      },
      onRecordingComplete: (videoData: CapturedVideoData, elapsedTime: number) => {
        // MediaRecorder stop events might be duplicated, so if the state already exists then just return
        if (capturedVideoData) {
          return;
        }

        EventsClient.logAmplitude('selfie.video.stop', {
          elapsedTime,
          type: mediaRecorderClient.type,
          chunksLength: videoData.type === 'upload' ? videoData.chunks?.length : 0,
        });

        if (
          videoData.type === 'upload' &&
          (videoData.chunks == null || videoData.chunks.length <= 0)
        ) {
          if (MediaRecorderManager.videoCanBeReattempted()) {
            handleRestartStep?.();
          }
          captureMessage('MediaRecorderClient error: no chunks recorded', 'info');
        } else {
          setCapturedVideoData(videoData);
        }
      },
      onStart: (isRestart: boolean) => {
        videoReady.current = true;
        setCapturedVideoData(undefined);

        EventsClient.logAmplitude('selfie.video.start', {
          isRestart,
          type: mediaRecorderClient.type,
        });
      },
      onTimeExceeded: () => {
        EventsClient.logAmplitude('selfie.video.timeExceeded');

        // Only throw back to the prompt page if capturing is not done yet
        if (!captureIsDone) {
          handleRestartStep?.({ timeExceeded: true });
        }
      },
    });

    mediaRecorderClient.startRecording();
    MediaRecorderManager.recordVideoAttempt();

    EventsClient.logAmplitude('selfie.video.initialized', {
      type: mediaRecorderClient.type,
      attempt: MediaRecorderManager.videoAttempts,
    });

    setMediaRecorderSubId(subId);
  };

  return {
    capturedVideoData,
    cleanUpVideoCapture: () => {
      MediaRecorderManager.destroy();
    },
    isAudioEnabled: () => {
      return enableVideoRecording && HolderClient.canRequestMicrophone;
    },
    isVideoReady: () => {
      return videoReady.current;
    },
    initVideoCapture: (stream: MediaStream, video: HTMLVideoElement | null | undefined) => {
      if (enableVideoRecording && !mediaRecorderSubId && video) {
        initMediaRecorderClient(stream, video);
      }
    },
    stopVideoCapture: () => {
      MediaRecorderManager.currentClient?.stopRecording();
    },
  };
};
